/**
 * The help browser.
 *
 * One topic per command: the original authors' words where they still hold,
 * and ours where the command is ours or works differently here — see
 * {@link ALL_TOPICS}. What is added here is the way in: an index you can read
 * down, and a search that answers while you type, because a help file nobody
 * can find anything in is a help file nobody opens.
 *
 * Search looks at the title, the command code and the body, and ranks a title
 * match above a body match — someone typing "flood" wants Flood Fill first and
 * the eleven commands that merely mention flooding afterwards.
 */

import { ALL_TOPICS, type HelpTopic } from '@sircad/core/menu/help-sircad.ts';
import { displayCode } from '../menu/runtime.ts';
import { openDialog } from './dialog.ts';

/** Which menu a code belongs to, for the index headings. */
const MENU_NAMES: Record<string, string> = {
  F: 'File', P: 'Place', G: 'Grab', E: 'Edit', D: 'Delete', U: 'Un-Do',
  S: 'Setup', B: 'Block', N: 'Nets', Z: 'Zoom', V: 'Views', '?': 'Help',
};

interface Scored { topic: HelpTopic; score: number }

/**
 * Rank the topics against a query.
 *
 * A title match beats a code match beats a body match, and an exact word beats
 * a fragment of one. Everything is lower-cased once rather than per comparison,
 * because this runs on every keystroke.
 */
function search(query: string, topics: HelpTopic[]): HelpTopic[] {
  const q = query.trim().toLowerCase();
  if (!q) return topics;

  const words = q.split(/\s+/);
  const scored: Scored[] = [];

  // Case matters in a command code and nowhere else: `Bt` is Cut to Clipboard
  // and `BT` is Track Isolation, so lower-casing them makes the two the same
  // topic and hands back whichever sorted first.
  const raw = query.trim();

  for (const topic of topics) {
    const title = plain(topic.title);
    const code = displayCode(topic.code).toLowerCase();
    const body = topic.body.toLowerCase();

    let score = 0;
    let missed = false;
    for (const w of words) {
      const word = plain(w);
      if (displayCode(topic.code) === raw) score += 120;
      else if (code === w) score += 100;
      else if (title === word) score += 90;
      else if (title.startsWith(word)) score += 60;
      else if (new RegExp(`\\b${escapeRe(word)}`).test(title)) score += 40;
      else if (title.includes(word)) score += 20;
      else if (body.includes(w)) score += 5;
      else { missed = true; break; }
    }
    // Every word has to appear somewhere: searching "flood line" should not
    // return everything that merely says "line".
    if (missed || score === 0) continue;
    scored.push({ topic, score });
  }

  scored.sort((a, b) =>
    b.score - a.score || a.topic.title.localeCompare(b.topic.title));
  return scored.map((s) => s.topic);
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A title reduced to what someone would type looking for it.
 *
 * The original hyphenates: Un-Do Event, Rat-nest, Ortho-line, Iso-islands. Type
 * "undo" and none of them answered — the word is there, with a hyphen through
 * the middle of it, so every title test failed and the command was found only
 * by a body mention, ranked below three topics that merely talk about it. The
 * most-used command in the program was fifth on its own name.
 */
function plain(s: string): string {
  return s.toLowerCase().replace(/-/g, '');
}

/**
 * One paragraph of a topic, as a list where it is one.
 *
 * A topic is stored as plain text, because the Gerber writer and the extractor
 * both read the same strings and neither wants markup. But steps to follow read
 * as steps and not as a wall of prose, so the two shapes that matter are
 * recognised on the way to the screen: a paragraph whose every line opens with
 * a number and a stop is an ordered list, and one whose every line opens with a
 * bullet is an unordered one. Anything else is a paragraph, as before.
 */
function paragraph(text: string): HTMLElement {
  const lines = text.split('\n');
  const ordered = lines.length > 1 && lines.every((l) => /^\d+\.\s/.test(l));
  const bulleted = lines.length > 1 && lines.every((l) => l.startsWith('· '));

  if (!ordered && !bulleted) {
    const p = document.createElement('p');
    p.textContent = text;
    return p;
  }

  const list = document.createElement(ordered ? 'ol' : 'ul');
  list.className = 'help-steps';
  for (const line of lines) {
    const li = document.createElement('li');
    li.textContent = line.replace(ordered ? /^\d+\.\s+/ : /^·\s+/, '');
    list.append(li);
  }
  return list;
}

export interface HelpOptions {
  /** Open straight at this command's topic, e.g. the command just run. */
  at?: string;
  /**
   * True when a command is built.
   *
   * Topics for anything else are left out altogether. A help file should
   * describe the program in front of you: an index that lists commands which
   * do nothing, however it labels them, is a list of disappointments.
   */
  implemented?(code: string): boolean;
}

export function showHelp(opts: HelpOptions = {}): void {
  openDialog({
    title: 'Help',
    build(body, handle) {
      const wrap = document.createElement('div');
      wrap.className = 'help-wrap';
      // Only what this program can actually do.
      const available = opts.implemented
        ? ALL_TOPICS.filter((t) => opts.implemented!(displayCode(t.code)))
        : ALL_TOPICS;

      body.append(wrap);

      const side = document.createElement('div');
      side.className = 'help-side';
      const pane = document.createElement('div');
      pane.className = 'help-pane';
      wrap.append(side, pane);

      const box = document.createElement('input');
      box.type = 'search';
      box.placeholder = `Search ${available.length} topics…`;
      box.autocomplete = 'off';
      box.spellcheck = false;
      side.append(box);

      const list = document.createElement('div');
      list.className = 'help-list';
      side.append(list);


      let shown: HelpTopic[] = [];
      let current = 0;

      const render = (topic: HelpTopic | undefined): void => {
        pane.replaceChildren();
        if (!topic) {
          const p = document.createElement('p');
          p.className = 'modal-hint';
          p.textContent = 'Nothing matches that.';
          pane.append(p);
          return;
        }
        const h = document.createElement('h3');
        h.textContent = topic.title;
        const code = document.createElement('span');
        code.className = 'help-code';
        code.textContent = displayCode(topic.code);
        h.append(code);
        pane.append(h);

        for (const para of topic.body.split('\n\n')) pane.append(paragraph(para));
      };

      const select = (i: number): void => {
        current = Math.max(0, Math.min(shown.length - 1, i));
        list.querySelectorAll('.help-row').forEach((r, n) => {
          r.classList.toggle('on', n === current);
        });
        list.querySelector('.help-row.on')?.scrollIntoView({ block: 'nearest' });
        render(shown[current]);
      };

      const fill = (query: string): void => {
        shown = search(query, available);
        list.replaceChildren();

        let heading = '';
        shown.forEach((t, i) => {
          // Grouped by menu while browsing; a search is ranked, so grouping it
          // would fight the ranking.
          if (!query.trim()) {
            const menu = MENU_NAMES[displayCode(t.code)[0] ?? ''] ?? 'Other';
            if (menu !== heading) {
              heading = menu;
              const h = document.createElement('div');
              h.className = 'help-heading';
              h.textContent = menu;
              list.append(h);
            }
          }
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'help-row';
          const c = document.createElement('span');
          c.className = 'help-code';
          c.textContent = displayCode(t.code);
          const label = document.createElement('span');
          label.textContent = t.title;
          row.append(c, label);
          row.addEventListener('click', () => select(i));
          list.append(row);
        });
        select(0);
      };

      box.addEventListener('input', () => fill(box.value));
      box.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowDown') { ev.preventDefault(); select(current + 1); }
        else if (ev.key === 'ArrowUp') { ev.preventDefault(); select(current - 1); }
        else if (ev.key === 'Enter') { ev.preventDefault(); select(current); }
        // Escape belongs to the dialog, which closes on it.
      });

      fill('');
      if (opts.at) {
        const i = shown.findIndex((t) => displayCode(t.code) === opts.at);
        if (i >= 0) select(i);
      }

      queueMicrotask(() => box.focus());
      void handle;
    },
  });
}
